/**
 * Today - the rep's day, read off the calendar.
 *
 * Every calendar entry the rep hosts or attends that day, each tied to the
 * facility it is a visit to (or a one-click "which facility?" when the entry
 * was booked without one); the follow-ups that fall due; a free-text note for
 * the day; and what has been logged so far.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { CalendarDays, CheckCircle2, ChevronLeft, ChevronRight, Link2, MapPin, NotebookPen } from 'lucide-react';

import { Button } from '../../../components/new-components/ui/button';
import { Textarea } from '../../../components/new-components/ui/textarea';
import { Input } from '../../../components/new-components/ui/input';
import { Account, Meta, Today, TodayEvent, bdApi, fmtDate, fmtTime, words } from './bdApi';
import { StagePill, StateLine } from './bdUi';

const addDays = (iso: string, n: number) => {
  const d = new Date(`${iso}T12:00:00Z`);
  d.setUTCDate(d.getUTCDate() + n);
  return d.toISOString().slice(0, 10);
};
const dayTitle = (iso: string) =>
  new Date(`${iso}T12:00:00Z`).toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', timeZone: 'UTC' });

export default function TodayView({ meta, onOpen }: { meta: Meta; onOpen: (facilityId: string, calendarEventId?: string) => void }) {
  const [date, setDate] = useState(meta.today);
  const [data, setData] = useState<Today | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    const r = await bdApi.today(date);
    setLoading(false);
    if (r.success) setData(r.data); else setError(r.error || 'Your day could not be loaded.');
  }, [date]);
  useEffect(() => { load(); }, [load]);

  const isToday = date === meta.today;

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center gap-2">
        <Button variant="outline" size="sm" aria-label="Previous day" onClick={() => setDate(addDays(date, -1))}><ChevronLeft className="w-4 h-4" /></Button>
        <h2 className="text-lg font-semibold min-w-[14rem]">{isToday ? 'Today · ' : ''}{dayTitle(date)}</h2>
        <Button variant="outline" size="sm" aria-label="Next day" onClick={() => setDate(addDays(date, 1))}><ChevronRight className="w-4 h-4" /></Button>
        {!isToday && <Button variant="ghost" size="sm" onClick={() => setDate(meta.today)}>Back to today</Button>}
      </div>

      <StateLine loading={loading && !data} error={error} onRetry={load} />

      {data && (
        <div className="grid gap-5 lg:grid-cols-[1fr_22rem]">
          <div className="space-y-5">
            <section aria-labelledby="visits">
              <h3 id="visits" className="text-sm font-semibold mb-2 flex items-center gap-2"><CalendarDays className="w-4 h-4" /> Visits and meetings</h3>
              {data.events.length === 0 ? (
                <p className="text-sm text-muted-foreground">Nothing on your calendar {isToday ? 'today' : 'this day'}. Plan a visit from any account.</p>
              ) : (
                <ol className="space-y-2">
                  {data.events.map((ev) => <EventRow key={ev.id} ev={ev} onOpen={onOpen} onLinked={load} />)}
                </ol>
              )}
            </section>

            <section aria-labelledby="followups">
              <h3 id="followups" className="text-sm font-semibold mb-2">Follow-ups due</h3>
              {data.follow_ups.length === 0 ? (
                <p className="text-sm text-muted-foreground">No next steps due on your accounts.</p>
              ) : (
                <ul className="divide-y rounded-lg border">
                  {data.follow_ups.map((a) => <FollowUpRow key={a.facility_id} a={a} today={meta.today} onOpen={onOpen} />)}
                </ul>
              )}
            </section>

            <section aria-labelledby="logged">
              <h3 id="logged" className="text-sm font-semibold mb-2">Logged {isToday ? 'today' : 'this day'}</h3>
              {data.activities.length === 0 ? (
                <p className="text-sm text-muted-foreground">Nothing logged yet.</p>
              ) : (
                <ul className="space-y-1 text-sm">
                  {data.activities.map((a) => (
                    <li key={a.id}>
                      <button type="button" className="text-left hover:underline" onClick={() => onOpen(a.facility_id)}>
                        <b>{words(a.kind)}</b> · {a.facility_name}{a.disposition ? ` · ${words(a.disposition)}` : ''}
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </section>
          </div>

          <DayNote date={date} initial={data.note} updatedAt={data.note_updated_at} />
        </div>
      )}
    </div>
  );
}

function EventRow({ ev, onOpen, onLinked }: { ev: TodayEvent; onOpen: (id: string, eventId?: string) => void; onLinked: () => void }) {
  const done = ev.status === 'completed' || ev.logged > 0;
  return (
    <li className="rounded-lg border p-3 flex flex-wrap items-start gap-3">
      <div className="w-20 shrink-0 text-sm" style={{ fontVariantNumeric: 'tabular-nums' }}>
        <div className="font-medium">{fmtTime(ev.starts_at)}</div>
        <div className="text-xs text-muted-foreground">{fmtTime(ev.ends_at)}</div>
      </div>
      <div className="min-w-0 flex-1">
        <div className="font-medium break-words">{ev.facility_name || ev.title}</div>
        {ev.facility_name && ev.title !== `Visit: ${ev.facility_name}` && <div className="text-xs text-muted-foreground break-words">{ev.title}</div>}
        {(ev.address || ev.facility_city) && (
          <div className="text-xs text-muted-foreground flex items-center gap-1"><MapPin className="w-3 h-3" /> {ev.address || ev.facility_city}</div>
        )}
        {ev.next_step && <div className="text-xs mt-1">Next step: {ev.next_step}{ev.next_step_on ? ` (${fmtDate(ev.next_step_on)})` : ''}</div>}
        {!ev.facility_id && <LinkFacility eventId={ev.id} onLinked={onLinked} />}
      </div>
      <div className="flex flex-col items-end gap-2">
        {ev.stage && <StagePill stage={ev.stage} />}
        {done && <span className="text-xs text-emerald-700 flex items-center gap-1"><CheckCircle2 className="w-3.5 h-3.5" /> Logged</span>}
        {ev.facility_id && (
          <Button size="sm" variant={done ? 'outline' : 'default'} onClick={() => onOpen(ev.facility_id as string, ev.id)}>
            {done ? 'Open account' : 'Log this visit'}
          </Button>
        )}
      </div>
    </li>
  );
}

/** A calendar entry booked without a facility: find it by name and tie them together. */
function LinkFacility({ eventId, onLinked }: { eventId: string; onLinked: () => void }) {
  const [q, setQ] = useState('');
  const [hits, setHits] = useState<Account[]>([]);
  const [open, setOpen] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => {
    if (q.trim().length < 3) { setHits([]); return; }
    clearTimeout(timer.current);
    timer.current = setTimeout(async () => {
      const r = await bdApi.accounts({ q, type: 'ALL', limit: 8 });
      setHits(r.success ? r.data.rows : []);
    }, 250);
  }, [q]);

  if (!open) {
    return (
      <button type="button" className="mt-1 text-xs underline underline-offset-2 flex items-center gap-1" onClick={() => setOpen(true)}>
        <Link2 className="w-3 h-3" /> Which facility is this?
      </button>
    );
  }
  return (
    <div className="mt-2 space-y-1">
      <Input autoFocus aria-label="Find the facility" placeholder="Type a facility name" value={q} onChange={(e) => setQ(e.target.value)} className="h-8" />
      {hits.length > 0 && (
        <ul className="rounded-md border bg-background text-sm max-h-48 overflow-auto">
          {hits.map((h) => (
            <li key={h.facility_id}>
              <button type="button" className="w-full text-left px-2 py-1 hover:bg-muted"
                onClick={async () => { const r = await bdApi.linkEvent(eventId, h.facility_id); if (r.success) onLinked(); }}>
                {h.name} <span className="text-muted-foreground">· {h.city} · {h.facility_type}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function FollowUpRow({ a, today, onOpen }: { a: Account; today: string; onOpen: (id: string) => void }) {
  const overdue = a.next_step_on && String(a.next_step_on).slice(0, 10) < today;
  return (
    <li>
      <button type="button" onClick={() => onOpen(a.facility_id)} className="w-full text-left px-3 py-2 hover:bg-muted/50 flex flex-wrap items-center gap-2">
        <span className="font-medium">{a.name}</span>
        <span className="text-xs text-muted-foreground">{a.city}</span>
        <span className="flex-1 min-w-[8rem] text-sm">{a.next_step || 'Follow up'}</span>
        <span className={`text-xs ${overdue ? 'text-destructive font-medium' : 'text-muted-foreground'}`}>{overdue ? 'Overdue · ' : ''}{fmtDate(a.next_step_on)}</span>
      </button>
    </li>
  );
}

function DayNote({ date, initial, updatedAt }: { date: string; initial: string; updatedAt: string | null }) {
  const [text, setText] = useState(initial);
  // What the server holds now - compared against, so a blur after a save does not save again.
  const [baseline, setBaseline] = useState(initial);
  const [saved, setSaved] = useState<string | null>(updatedAt);
  const [state, setState] = useState<'idle' | 'saving' | 'error'>('idle');
  const dirty = text !== baseline;
  useEffect(() => { setText(initial); setBaseline(initial); setSaved(updatedAt); }, [initial, updatedAt, date]);

  const savedLabel = useMemo(() => (saved ? `Saved ${new Date(saved).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}` : ''), [saved]);

  const save = async () => {
    if (!dirty) return;
    setState('saving');
    const sent = text;
    const r = await bdApi.saveDayNote(date, sent);
    if (r.success) { setBaseline(sent); setSaved(r.data.updated_at); setState('idle'); } else setState('error');
  };

  return (
    <section aria-labelledby="daynote" className="rounded-lg border p-4 space-y-2 h-fit">
      <h3 id="daynote" className="text-sm font-semibold flex items-center gap-2"><NotebookPen className="w-4 h-4" /> Notes for the day</h3>
      <Textarea
        aria-label="Notes for the day" rows={10} value={text} onChange={(e) => setText(e.target.value)}
        onBlur={save}
        placeholder="Route, who to call back, what to bring, how the day went"
      />
      <div className="flex items-center gap-3 text-xs">
        <Button size="sm" variant="outline" onClick={save} disabled={state === 'saving' || !dirty}>Save note</Button>
        <span role="status" className={state === 'error' ? 'text-destructive' : 'text-muted-foreground'}>
          {state === 'saving' ? 'Saving…' : state === 'error' ? 'Not saved - try again.' : savedLabel}
        </span>
      </div>
    </section>
  );
}
